import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  Unsubscribe
} from 'firebase/firestore';
import { db } from './firebase';
import { Category } from '../types';
import { sanitizeFirestoreData } from '../utils/sanitizeFirestore';

const COLLECTION_NAME = 'categories';

export function subscribeCategories(
  onData: (categories: Category[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const categoriesRef = collection(db, COLLECTION_NAME);
  return onSnapshot(
    categoriesRef,
    (snapshot) => {
      const categories: Category[] = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Omit<Category, 'id'>)
      }));
      onData(categories);
    },
    (err) => {
      console.error('Error listening to categories collection:', err);
      if (onError) onError(err);
    }
  );
}

export async function setCategory(category: Category): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, category.id);
  const sanitized = sanitizeFirestoreData(category);
  await setDoc(docRef, sanitized, { merge: true });
}

export async function addCategory(category: Category): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, category.id);
  const sanitized = sanitizeFirestoreData(category);
  await setDoc(docRef, sanitized);
}

export async function updateCategory(id: string, updates: Partial<Category>): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const { id: _ignoredId, ...safeUpdates } = updates;
  const sanitized = sanitizeFirestoreData(safeUpdates);
  await updateDoc(docRef, sanitized);
}

export async function deleteCategory(id: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(docRef);
}

export async function swapCategoryOrder(
  catA: { id: string; displayOrder: number },
  catB: { id: string; displayOrder: number }
): Promise<void> {
  const batch = writeBatch(db);
  batch.update(doc(db, COLLECTION_NAME, catA.id), { displayOrder: catA.displayOrder });
  batch.update(doc(db, COLLECTION_NAME, catB.id), { displayOrder: catB.displayOrder });
  await batch.commit();
}
